/**
 * Mengenformeln der Angebotskalkulation (ADR 0112).
 *
 * Die Formeln stammen aus der abgelösten Kalkulations-App (base44, Entität
 * „Berechnung") und sehen so aus:
 *
 *   ([Laenge]+[Wasserspiegel])*4
 *   (Math.ceil([Laenge]/5,874)*4)-4      ← Dezimalkomma!
 *   Math.sqrt([Laenge] * [Laenge] + [Wasserspiegel] * [Wasserspiegel])
 *
 * Kein `eval`: ein kleiner Parser für Zahlen, `[Feld]`, `+ - * /`, Klammern und
 * `Math.ceil|floor|round|sqrt|abs`. Ein Komma zwischen zwei Ziffern ist ein
 * Dezimalkomma — in JavaScript wäre es der Komma-Operator und `5,874` ergäbe still 874.
 */

export class QuantityFormulaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'QuantityFormulaError';
  }
}

const FUNCTIONS: Record<string, (value: number) => number> = {
  ceil: Math.ceil,
  floor: Math.floor,
  round: Math.round,
  sqrt: Math.sqrt,
  abs: Math.abs,
};

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'variable'; name: string }
  | { kind: 'function'; name: string }
  | { kind: 'operator'; value: '+' | '-' | '*' | '/' }
  | { kind: 'open' }
  | { kind: 'close' };

function tokenize(source: string): Token[] {
  const text = source.replace(/(\d),(\d)/g, '$1.$2');
  const tokens: Token[] = [];
  let index = 0;
  while (index < text.length) {
    const char = text[index]!;
    if (/\s/.test(char)) {
      index += 1;
      continue;
    }
    const numberMatch = /^(?:\d+(?:\.\d+)?|\.\d+)/.exec(text.slice(index));
    if (numberMatch) {
      tokens.push({ kind: 'number', value: Number(numberMatch[0]) });
      index += numberMatch[0].length;
      continue;
    }
    if (char === '[') {
      const end = text.indexOf(']', index);
      if (end < 0) throw new QuantityFormulaError(`Feldname ohne „]" in „${source.trim()}"`);
      const name = text.slice(index + 1, end).trim();
      if (!name) throw new QuantityFormulaError(`Leerer Feldname „[]" in „${source.trim()}"`);
      tokens.push({ kind: 'variable', name });
      index = end + 1;
      continue;
    }
    const functionMatch = /^Math\.([a-z]+)\s*\(/.exec(text.slice(index));
    if (functionMatch) {
      const name = functionMatch[1]!;
      if (!(name in FUNCTIONS)) throw new QuantityFormulaError(`Unbekannte Funktion „Math.${name}"`);
      tokens.push({ kind: 'function', name });
      index += functionMatch[0].length;
      continue;
    }
    if (char === '+' || char === '-' || char === '*' || char === '/') {
      tokens.push({ kind: 'operator', value: char });
    } else if (char === '(') {
      tokens.push({ kind: 'open' });
    } else if (char === ')') {
      tokens.push({ kind: 'close' });
    } else {
      throw new QuantityFormulaError(`Unerwartetes Zeichen „${char}" in „${source.trim()}"`);
    }
    index += 1;
  }
  return tokens;
}

/**
 * Rechnet eine Mengenformel aus. Wirft `QuantityFormulaError` bei Syntaxfehlern,
 * unbekannten Feldern und Ergebnissen, die keine endliche Zahl sind (z. B. ÷ 0).
 */
export function evaluateQuantityFormula(source: string, variables: Readonly<Record<string, number>>): number {
  const tokens = tokenize(source);
  if (tokens.length === 0) throw new QuantityFormulaError('Formel ist leer');
  let position = 0;

  const peek = () => tokens[position];
  const expectClose = () => {
    if (peek()?.kind !== 'close') throw new QuantityFormulaError(`„)" fehlt in „${source.trim()}"`);
    position += 1;
  };

  const expression = (): number => {
    let value = term();
    for (let token = peek(); token?.kind === 'operator' && (token.value === '+' || token.value === '-'); token = peek()) {
      position += 1;
      const right = term();
      value = token.value === '+' ? value + right : value - right;
    }
    return value;
  };

  const term = (): number => {
    let value = factor();
    for (let token = peek(); token?.kind === 'operator' && (token.value === '*' || token.value === '/'); token = peek()) {
      position += 1;
      const right = factor();
      value = token.value === '*' ? value * right : value / right;
    }
    return value;
  };

  const factor = (): number => {
    const token = peek();
    if (!token) throw new QuantityFormulaError(`Formel endet unerwartet: „${source.trim()}"`);
    position += 1;
    switch (token.kind) {
      case 'number':
        return token.value;
      case 'variable': {
        const value = variables[token.name];
        if (value === undefined) throw new QuantityFormulaError(`Unbekanntes Feld [${token.name}]`);
        return value;
      }
      case 'function': {
        const value = expression();
        expectClose();
        return FUNCTIONS[token.name]!(value);
      }
      case 'open': {
        const value = expression();
        expectClose();
        return value;
      }
      case 'operator':
        if (token.value === '-') return -factor();
        if (token.value === '+') return factor();
        throw new QuantityFormulaError(`„${token.value}" ohne linken Wert in „${source.trim()}"`);
      case 'close':
        throw new QuantityFormulaError(`„)" ohne „(" in „${source.trim()}"`);
    }
  };

  const result = expression();
  if (position < tokens.length) throw new QuantityFormulaError(`Unerwarteter Rest in „${source.trim()}"`);
  if (!Number.isFinite(result)) {
    throw new QuantityFormulaError(`Ergebnis ist keine Zahl (Division durch 0?) in „${source.trim()}"`);
  }
  return result;
}

/** Alle `[Feld]`-Namen einer Formel, in Reihenfolge des ersten Auftretens. */
export function listFormulaVariables(source: string): string[] {
  const names: string[] = [];
  for (const match of source.matchAll(/\[([^\]]+)\]/g)) {
    const name = match[1]!.trim();
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}
